import type { Task } from "@common/types/task";
import type { TaskDraftNeighbours } from "./taskBoardDraft";
import type { TaskPlaceValues } from "./taskBoardPlace";
import type { TaskBoardSort, TaskComparator } from "./taskBoardSort";

/**
 * Where a task created at a `+` stays: beside the cards its draft opened
 * between. Ties on the sort value order by task id and a new task has the
 * largest, so without this it'd sort after every card sharing its value.
 */
export interface TaskBoardPin {
    neighbours: TaskDraftNeighbours;
    /** The board sort it was created under. */
    sort: TaskBoardSort;
    /** The sort field's value it copied from its neighbour. */
    placeValues: TaskPlaceValues;
}

const isPinHeld = ({
    task,
    pin: { sort, placeValues },
    boardSort,
}: {
    task: Task;
    pin: TaskBoardPin;
    boardSort: TaskBoardSort;
}) =>
    sort === boardSort &&
    (placeValues.dueDate === undefined ||
        placeValues.dueDate === task.dueDate) &&
    (placeValues.priority === undefined ||
        placeValues.priority === task.priority);

/** One hop from a pinned task to the card it was created beside. */
interface PinStep {
    /** 1 when created below that card, -1 when above it. */
    side: 1 | -1;
    /** Creation order: a later create sits closer to the card. */
    order: number;
}

/** A task's place: the unpinned card its pins lead to, then the hops back. */
interface PinPath {
    root: Task;
    steps: PinStep[];
}

const comparePinSteps = (first: PinStep, second: PinStep) => {
    if (first.side !== second.side) {
        return first.side - second.side;
    }
    return first.side === 1
        ? second.order - first.order
        : first.order - second.order;
};

/**
 * `compareTasks` with each created task kept where its draft opened, while
 * the board keeps the sort it was created under and both the task and the
 * card it was created beside keep the value it copied. Kept only on this
 * board: a reload sorts it by id.
 */
export const pinTaskOrder = ({
    tasks,
    pins,
    sort,
    compareTasks,
}: {
    tasks: readonly Task[];
    pins: ReadonlyMap<string, TaskBoardPin>;
    sort: TaskBoardSort;
    compareTasks: TaskComparator;
}): TaskComparator => {
    if (pins.size === 0) {
        return compareTasks;
    }
    const tasksById = new Map(tasks.map((task) => [task.id, task]));
    const pinOrders = new Map([...pins.keys()].map((id, index) => [id, index]));
    const paths = new Map<string, PinPath>();

    const findPath = (task: Task): PinPath => {
        const known = paths.get(task.id);
        if (known !== undefined) {
            return known;
        }
        paths.set(task.id, { root: task, steps: [] });
        const pin = pins.get(task.id);
        const neighbour = pin?.neighbours.before ?? pin?.neighbours.after;
        const anchor =
            neighbour === undefined || neighbour === null
                ? undefined
                : tasksById.get(neighbour.id);
        const isHeld =
            pin !== undefined &&
            anchor !== undefined &&
            isPinHeld({ task, pin, boardSort: sort }) &&
            isPinHeld({ task: anchor, pin, boardSort: sort });
        if (!isHeld) {
            return { root: task, steps: [] };
        }
        const anchorPath = findPath(anchor);
        const path = {
            root: anchorPath.root,
            steps: [
                ...anchorPath.steps,
                {
                    side: pin.neighbours.before === null ? -1 : 1,
                    order: pinOrders.get(task.id) ?? 0,
                } satisfies PinStep,
            ],
        };
        paths.set(task.id, path);
        return path;
    };

    return (first, second) => {
        const firstPath = findPath(first);
        const secondPath = findPath(second);
        if (firstPath.root.id !== secondPath.root.id) {
            return compareTasks(firstPath.root, secondPath.root);
        }
        const depth = Math.max(firstPath.steps.length, secondPath.steps.length);
        for (let index = 0; index < depth; index++) {
            const firstStep = firstPath.steps[index];
            const secondStep = secondPath.steps[index];
            if (firstStep === undefined) {
                return -secondStep.side;
            }
            if (secondStep === undefined) {
                return firstStep.side;
            }
            if (firstStep.order !== secondStep.order) {
                return comparePinSteps(firstStep, secondStep);
            }
        }
        return 0;
    };
};
